// GoogleAuth.jsx

import React, { useEffect, useState } from "react";
import { Container, Button, Stack } from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import LogoutIcon from "@mui/icons-material/Logout";
import { supabase } from "../../supabaseClient";
import { LS_GOOGLE_ACCESS, LS_GOOGLE_REFRESH } from "./helpers";

export { GoogleIcon, LogoutIcon };

/** Save or update the user's Google refresh token in UsersStore (server reads it) */
export async function upsertUsersStoreRefresh(userId, refreshToken) {
    // keep: same table/columns you already use in your app
    if (!userId || !refreshToken) return;
    await supabase
        .from("UsersStore")
        .upsert(
            { id: userId, google_refresh_token: refreshToken },
            { onConflict: "id" },
        );
}

/** Always try the current provider access token, otherwise refresh using the Edge Function */
export async function getGoogleAccessToken() {
    const { data } = await supabase.auth.getSession();
    const session = data?.session || null;

    // 1) direct session/provider or cached localStorage
    const direct =
        session?.provider_token || localStorage.getItem(LS_GOOGLE_ACCESS);
    if (direct) return direct;

    // 2) refresh via server-side function
    return await refreshGoogleAccessToken();
}

/** Ask our Edge Function for a fresh Google access_token (server-side secure) */
export async function refreshGoogleAccessToken() {
    const { data } = await supabase.auth.getSession();
    const jwt = data?.session?.access_token || null;
    if (!jwt) return null;

    // Invoke your Edge Function. Important: pass the Supabase user JWT.
    const { data: fnData, error } = await supabase.functions.invoke(
        "google-token-exchange",
        {
            headers: { Authorization: `Bearer ${jwt}` },
        },
    );

    if (error || !fnData?.access_token) return null;

    // persist new access token for the rest of the app
    localStorage.setItem(LS_GOOGLE_ACCESS, fnData.access_token);
    return fnData.access_token;
}

export function useGoogleAuth() {
    const [session, setSession] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let active = true;

        supabase.auth.getSession().then(({ data: { session } }) => {
            if (!active) return;

            setSession(session);

            if (session?.provider_token) {
                localStorage.setItem(LS_GOOGLE_ACCESS, session.provider_token);
            }

            if (session?.provider_refresh_token) {
                localStorage.setItem(
                    LS_GOOGLE_REFRESH,
                    session.provider_refresh_token,
                );

                if (session.user?.id) {
                    upsertUsersStoreRefresh(
                        session.user.id,
                        session.provider_refresh_token,
                    );
                }
            }
        });

        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((event, session) => {
            setSession(session);

            if (
                (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") &&
                session?.provider_token
            ) {
                localStorage.setItem(LS_GOOGLE_ACCESS, session.provider_token);
            }

            if (
                (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") &&
                session?.provider_refresh_token
            ) {
                localStorage.setItem(
                    LS_GOOGLE_REFRESH,
                    session.provider_refresh_token,
                );

                if (session.user?.id) {
                    upsertUsersStoreRefresh(
                        session.user.id,
                        session.provider_refresh_token,
                    );
                }
            }

            if (event === "SIGNED_OUT") {
                localStorage.removeItem(LS_GOOGLE_ACCESS);
                localStorage.removeItem(LS_GOOGLE_REFRESH);
            }
        });

        return () => {
            active = false;
            subscription?.unsubscribe();
        };
    }, []);

    const loginAllInOne = async () => {
        setBusy(true);

        const { error } = await supabase.auth.signInWithOAuth({
            provider: "google",
            options: {
                scopes: "https://www.googleapis.com/auth/contacts.readonly",
                queryParams: {
                    prompt: "consent",
                    access_type: "offline",
                },
                redirectTo: window.location.href,
            },
        });

        if (error) {
            setBusy(false);
            throw error;
        }
    };

    const logout = async () => {
        try {
            setBusy(true);

            const { error } = await supabase.auth.signOut();

            if (error) {
                throw error;
            }
        } finally {
            setBusy(false);
        }
    };

    return {
        session,
        busy,
        loginAllInOne,
        logout,
    };
}

export default function GoogleAuth({ title = "Entrar con Google" }) {
    const { session, busy, loginAllInOne } = useGoogleAuth();

    if (session) return null;

    return (
        <Container
            maxWidth="xs"
            style={{ marginTop: "2rem", textAlign: "center" }}
        >
            <Stack spacing={2}>
                <Button
                    variant="contained"
                    onClick={loginAllInOne}
                    disabled={busy}
                    startIcon={<GoogleIcon />}
                >
                    {busy ? "Abriendo Google…" : title}
                </Button>
            </Stack>
        </Container>
    );
}
